import { useState } from 'react';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { auth, signOutUser, useAuth } from '@/lib/platform';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { useToast } from '@/components/ui/toast';
import { InfoNote } from '@/components/shared/info-note';
import { isUsableContactPhone, useContactPhone } from '@/lib/contact';

export const Route = createFileRoute('/_authed/account')({
  component: AccountPage,
});

/**
 * Account screen for the booking tool.
 *
 * There is no editable server profile any more — that was weebee's
 * /api/v1/users/me, which this app no longer calls. Identity is read-only from the
 * Google account; the one thing an operator sets here is the booking contact phone
 * (see lib/contact.ts for why that is mandatory).
 */
function AccountPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const authState = useAuth();
  const toast = useToast();
  const [storedPhone, setStoredPhone] = useContactPhone();
  const [phone, setPhone] = useState(storedPhone);

  const back = () => {
    if (router.history.canGoBack()) router.history.back();
    else void router.navigate({ to: '/' });
  };

  const user = auth.currentUser;
  const role = authState.status === 'authenticated' ? authState.role : '';
  const dirty = phone.trim() !== storedPhone;
  const valid = isUsableContactPhone(phone);

  function save() {
    if (!valid) {
      toast('Enter at least 10 digits — ONDC billing needs a real contact number.');
      return;
    }
    setStoredPhone(phone);
    toast('Booking contact saved.');
  }

  async function handleSignOut() {
    await signOutUser();
    void navigate({ to: '/login' });
  }

  return (
    <Screen title="Account" onBack={back}>
      <div className="wb-col wb-gap-14">
        <div className="wb-card wb-card--pad wb-col wb-gap-6">
          <div className="wb-h3">{user?.displayName ?? 'Signed in'}</div>
          {user?.email && <div className="wb-small wb-mono">{user.email}</div>}
          {role && <div className="wb-tiny">Role: {role}</div>}
        </div>

        <div className="wb-card wb-card--pad wb-col wb-gap-10">
          <div className="wb-h3">Booking contact</div>
          <InfoNote>
            Sent as the ONDC billing phone and prefilled into Razorpay. Google sign-in provides no
            phone number, and operators reject bookings without one — cancellation in particular.
          </InfoNote>
          <Field
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 90000 00000"
            inputMode="tel"
            error={phone && !valid ? 'Needs at least 10 digits.' : undefined}
          />
          <Button onClick={save} disabled={!dirty}>
            Save contact
          </Button>
        </div>

        <Button variant="secondary" onClick={() => void handleSignOut()}>
          Sign out
        </Button>
      </div>
    </Screen>
  );
}
